import React, { useState, useEffect, useCallback } from 'react';
import { Factory, Wrench, Plus, Trash2, Edit2, X, Check, Save } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  carousel: { label: 'Line', icon: Factory, color: 'bg-slate-800 text-white', badge: 'bg-slate-100 text-slate-700', border: 'border-slate-200' },
  table: { label: 'Table', icon: Wrench, color: 'bg-amber-600 text-white', badge: 'bg-amber-100 text-amber-700', border: 'border-amber-200' },
};

export default function LineManagement({ isAdmin }) {
  const [lines, setLines] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', station_count: 1, line_type: 'carousel' });

  const fetchLines = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/lines`);
      setLines(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchLines(); }, [fetchLines]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name || !form.code) { toast.error('Name and code are required'); return; }
    try {
      await axios.post(`${API}/api/lines`, {
        name: form.name,
        code: form.code,
        station_count: form.line_type === 'table' ? 1 : (form.station_count || 20),
        line_type: form.line_type,
      });
      toast.success(`${form.line_type === 'table' ? 'Table' : 'Line'} "${form.name}" created`);
      setShowAdd(false);
      setForm({ name: '', code: '', station_count: 1, line_type: 'carousel' });
      fetchLines();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error creating'); }
  };

  const handleDelete = async (id, name, type) => {
    if (!window.confirm(`Delete ${type === 'table' ? 'table' : 'line'} "${name}"? All associated stations will also be deleted.`)) return;
    try {
      await axios.delete(`${API}/api/lines/${id}`);
      toast.success(`"${name}" deleted`);
      fetchLines();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error deleting'); }
  };

  const handleUpdate = async (id) => {
    try {
      await axios.put(`${API}/api/lines/${id}`, {
        name: form.name,
        code: form.code,
        station_count: form.station_count,
        line_type: form.line_type,
      });
      toast.success('Updated');
      setEditingId(null);
      fetchLines();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error updating'); }
  };

  const startEdit = (line) => {
    setEditingId(line.id);
    setForm({ name: line.name, code: line.code, station_count: line.station_count, line_type: line.line_type });
    setShowAdd(false);
  };

  const lineItems = lines.filter(l => l.line_type !== 'table');
  const tableItems = lines.filter(l => l.line_type === 'table');

  const renderGroup = (items, type) => {
    const cfg = TYPE_CONFIG[type];
    const Icon = cfg.icon;
    return (
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`${type}-group`}>
        <div className={`px-5 py-3 border-b ${cfg.border} flex items-center gap-2 bg-slate-50`}>
          <Icon className="w-4 h-4 text-slate-600" />
          <span className="text-sm font-bold text-slate-700">{type === 'table' ? 'Production Tables' : 'Production Lines'}</span>
          <span className="text-[10px] bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full font-semibold ml-1">{items.length}</span>
        </div>
        {items.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-slate-400">
            No {type === 'table' ? 'tables' : 'lines'} yet. Click "Add" to create one.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map(l => {
              const isEditing = editingId === l.id;
              return (
                <div key={l.id} className="px-5 py-3 flex items-center gap-4 hover:bg-slate-50/50" data-testid={`line-row-${l.id}`}>
                  {isEditing ? (
                    <>
                      <input type="text" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                        className="border rounded-lg px-3 py-1.5 text-sm flex-1" placeholder="Name" />
                      <input type="text" value={form.code} onChange={e => setForm(f => ({...f, code: e.target.value}))}
                        className="border rounded-lg px-3 py-1.5 text-sm w-20" placeholder="Code" />
                      {type !== 'table' && (
                        <input type="number" value={form.station_count} onChange={e => setForm(f => ({...f, station_count: parseInt(e.target.value) || 1}))}
                          className="border rounded-lg px-3 py-1.5 text-sm w-24" placeholder="Stations" min={1} />
                      )}
                      <button onClick={() => handleUpdate(l.id)} className="p-1.5 bg-emerald-50 hover:bg-emerald-100 rounded-lg text-emerald-600">
                        <Check className="w-4 h-4" />
                      </button>
                      <button onClick={() => setEditingId(null)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400">
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold ${cfg.color}`}>
                        {l.code}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-slate-800">{l.name}</div>
                        <div className="text-[11px] text-slate-400">
                          {type === 'table' ? 'Workstation' : `${l.station_count} stations`}
                          {l.production_leader && ` · Leader: ${l.production_leader}`}
                        </div>
                      </div>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cfg.badge}`}>
                        {cfg.label}
                      </span>
                      {isAdmin && (
                        <div className="flex items-center gap-1">
                          <button onClick={() => startEdit(l)} className="p-1.5 hover:bg-blue-50 rounded-lg text-slate-400 hover:text-blue-600" data-testid={`edit-line-${l.id}`}>
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(l.id, l.name, l.line_type)} className="p-1.5 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-600" data-testid={`delete-line-${l.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="line-management-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Lines & Tables</h1>
          <p className="text-sm text-slate-500">Manage production lines and workstation tables</p>
        </div>
        {isAdmin && (
          <button onClick={() => { setShowAdd(true); setEditingId(null); setForm({ name: '', code: '', station_count: 20, line_type: 'carousel' }); }}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-900"
            data-testid="add-line-btn">
            <Plus className="w-4 h-4" /> Add Line / Table
          </button>
        )}
      </div>

      {/* Add Form */}
      {showAdd && isAdmin && (
        <div className="bg-white border rounded-xl p-5 mb-5 shadow-sm" data-testid="add-line-form">
          <h3 className="text-sm font-bold text-slate-700 mb-3">New Line / Table</h3>
          <form onSubmit={handleAdd} className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Type *</label>
              <select value={form.line_type} onChange={e => {
                const lt = e.target.value;
                setForm(f => ({...f, line_type: lt, station_count: lt === 'table' ? 1 : 20}));
              }} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="line-type-select">
                <option value="carousel">Production Line</option>
                <option value="table">Production Table</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Name *</label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                placeholder={form.line_type === 'table' ? 'Table 1' : 'Carousel Line 4'}
                data-testid="line-name-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Code *</label>
              <input type="text" value={form.code} onChange={e => setForm(f => ({...f, code: e.target.value.toUpperCase()}))}
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                placeholder={form.line_type === 'table' ? 'T1' : 'C4'}
                data-testid="line-code-input" />
            </div>
            {form.line_type !== 'table' && (
              <div>
                <label className="text-xs font-medium text-slate-500">Stations</label>
                <input type="number" value={form.station_count} onChange={e => setForm(f => ({...f, station_count: parseInt(e.target.value) || 1}))}
                  className="w-full border rounded-lg px-3 py-2 text-sm mt-1" min={1}
                  data-testid="line-stations-input" />
              </div>
            )}
            <div className="col-span-2 sm:col-span-4 flex justify-end gap-2 mt-1">
              <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button type="submit" className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1.5" data-testid="save-line-btn">
                <Save className="w-3.5 h-3.5" /> Create
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lines & Tables */}
      <div className="space-y-4">
        {renderGroup(lineItems, 'carousel')}
        {renderGroup(tableItems, 'table')}
      </div>
    </div>
  );
}
